<template>
  <div @click.shift="editRessource">
    <div v-if="!isAbstract" class="d-flex">
      <v-col class="align-start px-0">
        <v-row class="m-0">
          <div v-if="imagePath" class="me-3">
            <v-img :src='imagePath' class="header-logo">
            </v-img>
          </div>
          <div v-if="!disableExtend && !dialog" class="d-flex align-center pe-4">
            <v-icon v-if="isExtended" @click="toggleExtended" class="section-icon">
              fas fa-chevron-up
            </v-icon>
            <v-icon v-else @click="toggleExtended" class="section-icon">
              fas fa-chevron-down
            </v-icon>
          </div>


          <div v-if="!dialog" class="section-titre d-flex align-center">
            {{ ressource ? ressource.label : '' }}
          </div>

          <div v-if="showHelp" class="d-flex align-center px-3">
            <v-icon class="help-icon" @click="toggleHelpDialog">fas fa-circle-info</v-icon>
          </div>

          <!-- header when in dialog -->
          <div v-if="dialog" class="d-flex align-center">
            <span class="titre1">{{ ressource ? ressource.label : '' }}</span>
            <button-plus v-if="canAdd" class="ms-4" @callback=callback></button-plus>
          </div>

          <v-col v-if="isExtended && showSearch">
            <list-header @search="onSearchTextUpdated" @cardView="cardView" :showSearch="showSearch" :show-toggle="showToggle"></list-header>
          </v-col>
        </v-row>
      </v-col>

      <v-col class="align-content-center px-0" cols="auto">
        <v-row class="m-0 align-center justify-end">
          <div v-if="canAdd && isExtended">
            <loader-button @callback="callback" :label="addLabel ? addLabel : ''"
                           :disabled="addDisabled"></loader-button>
          </div>
          <div v-if="chat" class="pr-2">
            <v-btn class='base pl-9 pr-9'
                   variant="flat"
                   @click="openChat">
              <v-row>
                <div>
                  <v-icon size="18" color="white">
                    fas fa-comment
                  </v-icon>
                </div>
                <div class="px-2">{{ chatLabel }}</div>
                <div style="background-color: white; color: red; height: 20px; width: 20px; border-radius: 50%">
                  {{ unreadChatCount }}
                </div>
              </v-row>
            </v-btn>
          </div>

          <v-icon v-if="isExtended && maximise" @click="toggleFullscreen" class="section-icon">
            fas fa-maximize
          </v-icon>
          <v-icon v-if="dialog && !preventClose"
                  data-testid="section-close"
                  role="button"
                  aria-label="Fermer"
                  @click="closeDialog"
                  class="header-text">
            fas fa-xmark
          </v-icon>
        </v-row>
      </v-col>
    </div>
    <div v-else class="d-flex">
      <v-col class="align-start px-0">
        <v-row class="m-0">
          <v-col class="section-titre d-flex align-center">
            {{ ressource ? ressource.label : '' }}
          </v-col>

          <v-col data-testid="section-close"
                 role="button"
                 aria-label="Fermer"
                 @click.stop="closeDialog"
                 class="align-content-center text-end"
                 style="color: #E31F1C">
            Fermer
          </v-col>
        </v-row>
      </v-col>
    </div>
    <help-dialog v-if="showHelpDialog && ressource && ressource.helpHtmlId > 0"
                 :open="showHelpDialog"
                 :content="helpHtml"
                 @close="toggleHelpDialog">
    </help-dialog>
  </div>
</template>

<script lang="ts">
  import { Extended } from '@/framework/base/classes/Schema';
  import ButtonPlus from '@/framework/base/components/ButtonPlus.vue';
  import { type EditRessource, MapRessource } from '@/framework/base/helpers/Ressources';
  import { Ressources } from '@/framework/base/helpers/Ressources';
  import HelpDialog from '@/framework/base/components/HelpDialog.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SearchFilterInput from './SearchFilterInput.vue';
  import { defineComponent, getCurrentInstance, type PropType } from "vue";
  import { storeSession } from '@/generated/stores';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';
  import ListHeader from "@/framework/base/components/ListHeader.vue";

  export let cbEditRessource: EditRessource | undefined = undefined;

  export function setEditRessource(editRessource: EditRessource) {
    cbEditRessource = editRessource;
  }

  export default defineComponent({
    components: {
      ListHeader,
      HelpDialog,
      ButtonPlus,
      LoaderButton,
      SearchFilterInput
    },
    props: {
      ressource: {
        type: Object as PropType<MapRessource>,
        required: false
      },
      isExtended: {
        type: Boolean
      },
      disableExtend: {
        type: Boolean
      },
      dialog: {
        type: Boolean
      },
      imgPath: {
        type: String
      },
      canAdd: {
        type: Boolean
      },
      addLabel: {
        type: String,
        required: false
      },
      addDisabled: {
        type: Boolean
      },
      maximise: {
        type: Boolean
      },
      chat: {
        type: Boolean
      },
      chatLabel: {
        type: String,
        required: false
      },
      newChatItem: {
        type: Boolean
      },
      unreadChatCount: {
        type: Number
      },
      fiche: {
        type: String,
      },
      showSearch: {
        type: Boolean
      },
      preventClose: {
        type: Boolean
      },
      showToggle: {
        type: Boolean
      },
      isAbstract: {
        type: Boolean
      },
    },
    data() {
      return {
        cbEditRessource,
        loadedHelpHtml: '',
        showHelpDialog: false,
        dialogIn: true
      };
    },
    computed: {
      showHelp(): boolean {
        return this.ressource != undefined && this.ressource && this.ressource.helpHtmlId > 0 && this.loadedHelpHtml != "<div><br></div>";
      },
      helpHtml(): string {
        if ((this.showHelpDialog || this.dialogIn) && this.loadedHelpHtml == '' && this.ressource && this.ressource.helpHtmlId > 0) {
          this.getHelpResources(this.ressource.helpHtmlId);
        }
        return this.loadedHelpHtml;
      },
      imagePath(): string {
        let res = '';
        if (this.imgPath) {
          res = "../../src/" + this.imgPath;
        }
        return res;
      },
    },
    methods: {
      getHelpResources(value: number) {
        Ressources.getHelp(value).then((res) => {
          this.loadedHelpHtml = res.html;
        });
      },
      toggleExtended(): void {
        const instance = getCurrentInstance();
        let ref = '';
        if (instance) {
          const vnode = instance.vnode;
          ref = vnode.ref ? vnode.ref.toString() : '';
          this.$emit('extended', new Extended(ref, this.fiche, !this.isExtended));
        }
      },
      closeDialog(): void {
        this.$emit('closeDialog');
      },
      callback(): void {
        this.$emit('callback');
      },
      toggleFullscreen(): void {
        this.$emit('fullscreen');
      },
      openChat(): void {
        this.$emit('openChat');
      },
      onSearchTextUpdated(text: string): void {
        this.$emit('search', text);
      },
      cardView(value: boolean): void {
        this.$emit('cardView', value);
      },
      editRessource() {
        const user = storeSession.connectedUser;
        if (user) {
          const isAdmin = UserProfile.hasProfile(user, EnumProfile.admin);
          if (isAdmin && cbEditRessource && this.ressource) {
            cbEditRessource(this.ressource, this.updateHelp);
          }
        }
      },
      toggleHelpDialog() {
        this.showHelpDialog = !this.showHelpDialog;
      },
      updateHelp() {
        this.loadedHelpHtml = '';
      }
    }
  })

</script>

<style scoped>
  .header-logo {
    width: 100px;
    margin-left: 18px;
    object-fit: contain;
  }

  .section-titre {
    font-size: 25px;
    color: #979797;
  }
</style>
